import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '../../services/api';
import { Notification, NotificationStats } from '../../types';
import { formatUTCDateToLocal } from '../../utils/timezone';
import './NotificationsPage.css';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    setMessage('');
    try {
      const [notificationsResponse, statsResponse] = await Promise.all([
        notificationApi.getAll({ 
          type: filter === 'all' ? undefined : filter,
          limit: '50'
        }),
        notificationApi.getStats()
      ]);
      
      if (notificationsResponse.success && notificationsResponse.data) {
        setNotifications(notificationsResponse.data);
      } else {
        setNotifications([]);
      }
      
      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      setMessage('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  const getNotificationIcon = (type: string) => {
    if (!type) return '📢';
    switch (type) {
      case 'auto_checkout':
        return '🔄';
      case 'reminder':
        return '⏰';
      case 'error':
        return '❌';
      default:
        return '📢';
    }
  };

  const getNotificationTypeColor = (type: string) => {
    if (!type) return '#6b7280';
    switch (type) {
      case 'auto_checkout':
        return '#3b82f6';
      case 'reminder':
        return '#f59e0b';
      case 'error':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown Time';
    try {
      return formatUTCDateToLocal(dateString);
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <div className="notifications-container">
      <div className="notifications-card">
        <div className="notifications-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="notifications-title">Notifications</h1>
          <p className="notifications-subtitle">
            View notification history and logs for auto-checked out people
          </p>
          <p className="data-period-note">Showing notifications from the last 30 days</p>
        </div>

        <div className="notifications-content">
          {/* Stats Cards */}
          {stats && (
            <div className="stats-section">
              <div className="stat-card">
                <div className="stat-number">{stats.totalNotifications || stats.total_notifications || 0}</div>
                <div className="stat-label">Total Notifications</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.autoCheckoutCount || stats.auto_checkout_count || 0}</div>
                <div className="stat-label">Auto-Checkouts</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.reminderCount || stats.reminder_count || 0}</div>
                <div className="stat-label">Reminders</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.errorCount || stats.error_count || 0}</div>
                <div className="stat-label">Errors</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.recentNotifications || stats.recent_notifications || 0}</div>
                <div className="stat-label">Last 24h</div>
              </div>
            </div>
          )}

          {/* Filter Controls */}
          <div className="filter-controls">
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${filter === 'auto_checkout' ? 'active' : ''}`}
                onClick={() => setFilter('auto_checkout')}
              >
                Auto-Checkouts
              </button>
              <button 
                className={`filter-btn ${filter === 'reminder' ? 'active' : ''}`}
                onClick={() => setFilter('reminder')}
              >
                Reminders
              </button>
              <button 
                className={`filter-btn ${filter === 'error' ? 'active' : ''}`}
                onClick={() => setFilter('error')}
              >
                Errors
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="notifications-list">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading notifications...</p>
              </div>
            ) : !notifications || notifications.length === 0 ? (
              <div className="empty-state">
                <p>No notifications found</p>
              </div>
            ) : (
              notifications.filter(notification => notification && notification.id).map((notification) => {
                // Handle both camelCase and snake_case field names from API
                const notificationType: string = notification.notificationType || notification.notification_type || 'unknown';
                const createdAt: string = notification.createdAt || notification.created_at || '';
                const userName = notification.userName || notification.user_name;
                const userEmail = notification.userEmail || notification.user_email;
                const userId = notification.userId || notification.user_id;
                
                return (
                  <div key={notification.id} className="notification-item">
                    <div className="notification-icon" style={{ color: getNotificationTypeColor(notificationType as string) }}>
                      {getNotificationIcon(notificationType as string)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-header">
                        <h3 className="notification-title">{notification.title || 'No Title'}</h3>
                        <span className="notification-time">{formatDate(createdAt)}</span>
                      </div>
                      <p className="notification-message">{notification.message || 'No message'}</p>
                      {userName && (
                        <div className="notification-user">
                          <strong>User:</strong> {userName}
                          {notification.passcode && ` (${notification.passcode})`}
                        </div>
                      )}
                      <div className="notification-meta">
                        <span className={`notification-type ${notificationType}`}>
                          {notificationType.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className={`notification-status ${notification.status || 'unknown'}`}>
                          {notification.status ? notification.status.toUpperCase() : 'UNKNOWN'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {message && (
            <div className="error-message">
              {message}
            </div>
          )}
        </div>

        <div className="notifications-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
